import { Title, Tag, Text, Box, Link, Icon } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import {
  InvoicesVariantResponseDto,
  InvoicesVariantRequestDto,
} from '@tiendanube/common';
import { CardCollapsible, ErrorScreen, Stack } from 'commons/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { useGetStoreInvoicesQuery } from 'domains/Invoices/invoicesApi';
import {
  TypesRadio,
  NcmAndCest,
  OriginSelect,
  InvoiceSettingsAlert,
} from './components';
import { TaxRules } from './components/TaxRules';
import { useProductDetails } from '../../EditProductPage/hooks';
import { OnChangeToAllVariants } from '../Variants/types';

interface InvoiceSettingsCardProps {
  onChange: (data: InvoicesVariantRequestDto) => void;
  invoiceSettings?: Partial<InvoicesVariantResponseDto>;
  variantId: string;
  onChangeToAllVariants?: OnChangeToAllVariants;
  productId?: string;
}

function InvoiceSettingsCard({
  productId = '',
  onChange,
  invoiceSettings,
  variantId,
  onChangeToAllVariants,
}: Readonly<InvoiceSettingsCardProps>) {
  const t = useTranslationCatalog('products.detail.invoiceSettings');
  const { fetchProduct, product } = useProductDetails(productId);

  const defaultInvoiceSettings: InvoicesVariantResponseDto = {
    id: 0,
    origin: '',
    product_type: '',
    ncm: '',
    cest: '',
  };
  const safeValues = invoiceSettings ?? defaultInvoiceSettings;

  const { data: storeData } = useGetStoreInvoicesQuery();

  const onChangeInvoiceVariant = (data: InvoicesVariantRequestDto) => {
    onChange(data);
  };

  const errorToGetInvoiceSettings = product?.invoiceSettingsError?.get ?? false;

  return (
    <CardCollapsible
      title={
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Title as="h4">{t('title')}</Title>
          <Box paddingRight="2">
            <Tag appearance="primary">{t('tag')}</Tag>
          </Box>
        </Box>
      }
      active={false}
      cursorEnable
    >
      {errorToGetInvoiceSettings && (
        <ErrorScreen
          description={t('errorTaxRules.title')}
          onRetry={fetchProduct}
        />
      )}

      {!errorToGetInvoiceSettings && (
        <Stack column align="stretch" spacing="base">
          {storeData?.status === 'INCOMPLETE' && <InvoiceSettingsAlert />}
          <Text>{t('description')}</Text>
          <OriginSelect
            value={safeValues.origin}
            onChange={onChangeInvoiceVariant}
            onChangeToAllVariants={onChangeToAllVariants}
          />
          <TypesRadio
            value={safeValues.product_type}
            onChange={onChangeInvoiceVariant}
            onChangeToAllVariants={onChangeToAllVariants}
          />
          <NcmAndCest
            valueNcm={safeValues.ncm}
            valueCest={safeValues.cest}
            onChange={onChangeInvoiceVariant}
            onChangeToAllVariants={onChangeToAllVariants}
          />
          <TaxRules variantId={variantId} />
          <Stack.Item>
            <Link
              as="a"
              href="https://nimbus.tiendanube.com/"
              target="_blank"
              appearance="neutral"
              textDecoration="underline"
            >
              {t('helpLink')}
              <Icon source={<ExternalLinkIcon />} color="currentColor" />
            </Link>
          </Stack.Item>
        </Stack>
      )}
    </CardCollapsible>
  );
}

export default InvoiceSettingsCard;
